import {
   buildExamQuestionPutPayload,
   toPositiveInt,
} from "./examQuestionFormat";

const MIN_ANSWERS = 2;

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function firstNonEmpty(...values) {
   for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
   }
   return "";
}

function normalizeLooseKey(value) {
   return String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
}

function getValueByLooseKeys(obj, keys = []) {
   if (!isObject(obj)) return undefined;
   const normalized = keys.map((key) => normalizeLooseKey(key));

   for (const [key, value] of Object.entries(obj)) {
      if (normalized.includes(normalizeLooseKey(key))) {
         return value;
      }
   }

   return undefined;
}

function parseJsonArraySafe(value, fallback = []) {
   if (Array.isArray(value)) return value;
   if (typeof value !== "string") return fallback;

   const text = String(value ?? "").trim();
   if (!text) return fallback;

   try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : fallback;
   } catch {
      return fallback;
   }
}

function parseLocalizedTextRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO, value?.textRo, value?.text_ro),
         ru: firstNonEmpty(value?.ru, value?.RU, value?.textRu, value?.text_ru),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedTextRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

function parseLocalizedAnswerRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO),
         ru: firstNonEmpty(value?.ru, value?.RU),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedAnswerRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

function inferCorrectAnswerFromAnswerFlags(answersBase = []) {
   for (let i = 0; i < answersBase.length; i += 1) {
      const answer = answersBase[i];
      if (!isObject(answer)) continue;

      const flag =
         answer.correct ??
         answer.isCorrect ??
         answer.right ??
         answer.ok ??
         answer.valid ??
         false;

      if (flag === true) return i + 1;
   }

   return null;
}

function normalizeImportCorrectAnswer(rawValue, answersCount) {
   const text = String(rawValue ?? "").trim();
   if (!text) return null;

   let n = null;

   if (/^[+-]?\d+$/.test(text)) {
      n = Number(text);
   } else {
      const upper = text.toUpperCase();

      if (/^[A-F]$/.test(upper)) {
         n = upper.charCodeAt(0) - 64;
      } else {
         const romanMap = {
            I: 1,
            II: 2,
            III: 3,
            IV: 4,
            V: 5,
            VI: 6,
         };
         if (Object.prototype.hasOwnProperty.call(romanMap, upper)) {
            n = romanMap[upper];
         } else {
            const nums = text.match(/\d+/g);
            if (nums?.length === 1) {
               n = Number(nums[0]);
            }
         }
      }
   }

   if (!Number.isInteger(n)) return null;
   if (n >= 1 && n <= answersCount) return n;
   if (n >= 0 && n < answersCount) return n + 1;
   return null;
}

export function extractTicketNumber(value) {
   const text = String(value ?? "").trim();
   if (!text) return null;

   const patterns = [
      /\bPractice\s*P\s*(\d+)\b/i,
      /\bP\s*(\d+)\b/i,
      /\bB(?:ilet)?\s*#?\s*(\d+)\b/i,
      /\bBiletul\s*#?\s*(\d+)\b/i,
      /^B(\d+)$/i,
      /^P(\d+)$/i,
      /^(\d+)$/,
   ];

   for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match?.[1]) {
         const parsed = toPositiveInt(match[1]);
         if (parsed) return parsed;
      }
   }

   const fallback = text.match(/\b(\d+)\b/);
   return fallback ? toPositiveInt(fallback[1]) : null;
}

function readPracticeTicketIdRange() {
   const start = Number(
      process.env.REACT_APP_TICKETS_START ||
         process.env.VITE_TICKETS_START ||
         246,
   );
   const count = Number(
      process.env.REACT_APP_TICKETS_COUNT ||
         process.env.VITE_TICKETS_COUNT ||
         24,
   );
   const safeCount = Number.isInteger(count) && count > 0 ? count : 24;
   const safeStart = Number.isInteger(start) && start > 0 ? start : 246;

   return {
      start: safeStart,
      end: safeStart + safeCount - 1,
      count: safeCount,
   };
}

export function resolvePracticeTicketNumber(ticketOrName, ticketId = null) {
   const name =
      typeof ticketOrName === "object"
         ? String(
              ticketOrName?.name ??
                 ticketOrName?.ticketName ??
                 ticketOrName?.title ??
                 "",
           ).trim()
         : String(ticketOrName ?? "").trim();
   const id =
      typeof ticketOrName === "object"
         ? toPositiveInt(ticketOrName?.id ?? ticketOrName?.ticketId ?? ticketId)
         : toPositiveInt(ticketId);

   const fromName = extractTicketNumber(name);
   if (fromName && fromName >= 1 && fromName <= 24) return fromName;

   const range = readPracticeTicketIdRange();
   if (
      Number.isInteger(id) &&
      id >= range.start &&
      id <= range.end
   ) {
      return id - range.start + 1;
   }

   return fromName;
}

function normalizeImportQuestion(raw, index, ticketNo = null) {
   if (!isObject(raw)) {
      throw new Error(`Întrebarea #${index + 1} nu este un obiect valid.`);
   }

   const questionId = toPositiveInt(
      raw?.questionId ?? raw?.question_id ?? raw?.id ?? raw?.qid,
   );
   const order =
      toPositiveInt(
         raw?.order ??
            raw?.ordine ??
            raw?.position ??
            raw?.index ??
            raw?.nr,
      ) ?? index + 1;

   const textLocalized = parseLocalizedTextRaw(
      raw?.text ??
         raw?.intrebare ??
         raw?.întrebare ??
         raw?.question ??
         raw?.questionText,
   );
   const textRo = firstNonEmpty(
      raw?.textRo,
      raw?.text_ro,
      raw?.textRO,
      textLocalized.ro,
   );
   const textRu = firstNonEmpty(
      raw?.textRu,
      raw?.text_ru,
      raw?.textRU,
      textLocalized.ru,
   );

   if (!textRo && !textRu) {
      throw new Error(`Întrebarea #${order}: lipsește textul (RO/RU).`);
   }

   const answersBase = parseJsonArraySafe(
      raw?.answers ?? raw?.raspunsuri ?? raw?.răspunsuri,
      [],
   );
   const answersRuBase = parseJsonArraySafe(
      raw?.answersRu ?? raw?.answersRU ?? raw?.answers_ru ?? raw?.raspunsuriRu,
      [],
   );

   if (!answersBase.length && !answersRuBase.length) {
      throw new Error(`Întrebarea #${order}: lipsesc răspunsurile.`);
   }

   const maxAnswerSource = Math.max(answersBase.length, answersRuBase.length);
   const answers = [];

   for (let i = 0; i < maxAnswerSource; i += 1) {
      const roObj = parseLocalizedAnswerRaw(answersBase[i]);
      const ruObj = parseLocalizedAnswerRaw(answersRuBase[i]);

      const ro = firstNonEmpty(roObj?.ro, roObj?.ru);
      const ru = firstNonEmpty(ruObj?.ru, ruObj?.ro, roObj?.ru);

      if (!ro && !ru) continue;

      answers.push({
         ro: ro || ru,
         ru: ru || ro,
      });
   }

   if (answers.length < MIN_ANSWERS) {
      throw new Error(
         `Întrebarea #${order}: sunt necesare minimum ${MIN_ANSWERS} răspunsuri.`,
      );
   }

   const rawCorrectAnswer =
      getValueByLooseKeys(raw, [
         "correctAnswer",
         "correct_answer",
         "correctIndex",
         "correct_index",
         "rightIndex",
         "right_index",
         "answerIndex",
         "answer_index",
         "rightAnswer",
         "right_answer",
         "goodAnswer",
         "good_answer",
         "solution",
         "answer",
         "raspunsCorect",
         "raspuns_corect",
         "raspunsulCorect",
         "raspunsul_corect",
      ]) ??
      inferCorrectAnswerFromAnswerFlags(answersBase) ??
      null;

   const parsedCorrectAnswer = normalizeImportCorrectAnswer(
      rawCorrectAnswer,
      answers.length,
   );
   const correctAnswer = parsedCorrectAnswer || 1;

   const image =
      firstNonEmpty(raw?.image, raw?.img) ||
      (ticketNo
         ? `/EXAMEN_INSTRUIRE_AUTO/images/B${ticketNo}/image${order}.jpeg`
         : "");

   const categoryId =
      raw?.categoryId == null ? null : (toPositiveInt(raw?.categoryId) ?? null);

   const draft = {
      textRo,
      textRu,
      image,
      answers,
      correctAnswer,
      categoryId,
   };

   return {
      questionId,
      order,
      correctAnswerFallbackUsed: !parsedCorrectAnswer,
      draft,
      payload: buildExamQuestionPutPayload(draft),
   };
}

function normalizeTicketQuestions(rawQuestions, ticketNo = null) {
   const usedOrders = new Set();

   const items = safeList(rawQuestions)
      .map((item, index) => normalizeImportQuestion(item, index, ticketNo))
      .sort((left, right) => Number(left.order) - Number(right.order));

   return items.map((item, index) => {
      let nextOrder = toPositiveInt(item.order);
      if (!nextOrder || usedOrders.has(nextOrder)) {
         nextOrder = index + 1;
      }
      usedOrders.add(nextOrder);
      return { ...item, order: nextOrder };
   });
}

function normalizeTicketEntry(raw, key = "", index = 0) {
   if (!isObject(raw)) return null;

   const ticketId = toPositiveInt(
      raw?.ticketId ?? raw?.ticket_id ?? raw?.id ?? raw?.biletId,
   );
   const ticketKey = String(
      raw?.ticket ??
         raw?.bilet ??
         raw?.code ??
         raw?.label ??
         raw?.name ??
         key ??
         "",
   ).trim();

   const ticketNo =
      toPositiveInt(raw?.ticketNo ?? raw?.ticket_no ?? raw?.biletNo) ||
      extractTicketNumber(ticketKey) ||
      extractTicketNumber(raw?.name) ||
      null;

   const questions = normalizeTicketQuestions(
      safeList(
         raw?.questions ??
            raw?.intrebari ??
            raw?.întrebări ??
            raw?.items ??
            raw?.entries,
      ),
      ticketNo,
   );

   if (!questions.length && !ticketId && !ticketKey && !ticketNo) {
      return null;
   }

   return {
      ticketId,
      ticketKey: ticketKey || (ticketNo ? `B${ticketNo}` : `ticket-${index + 1}`),
      ticketNo,
      ticketName: firstNonEmpty(raw?.ticketName, raw?.name, raw?.title),
      questions,
   };
}

function flattenTicketEntries(parsed) {
   if (Array.isArray(parsed)) {
      if (
         parsed.length &&
         isObject(parsed[0]) &&
         safeList(parsed[0]?.questions ?? parsed[0]?.intrebari).length
      ) {
         return parsed
            .map((item, index) => normalizeTicketEntry(item, "", index))
            .filter(Boolean);
      }

      return [
         {
            ticketKey: "selected",
            ticketId: null,
            ticketNo: null,
            ticketName: null,
            questions: normalizeTicketQuestions(parsed),
         },
      ];
   }

   if (!isObject(parsed)) return [];

   const directQuestions = safeList(
      parsed?.questions ??
         parsed?.intrebari ??
         parsed?.întrebări ??
         parsed?.items,
   );
   if (directQuestions.length) {
      const ticketNo =
         extractTicketNumber(parsed?.ticket ?? parsed?.bilet) ||
         toPositiveInt(parsed?.ticketNo);
      return [
         {
            ticketKey: firstNonEmpty(parsed?.ticket, parsed?.bilet, "selected"),
            ticketId: toPositiveInt(parsed?.ticketId ?? parsed?.ticket_id),
            ticketNo,
            ticketName: firstNonEmpty(parsed?.name, parsed?.title),
            questions: normalizeTicketQuestions(directQuestions, ticketNo),
         },
      ];
   }

   const bucketSources = [
      parsed?.bilete,
      parsed?.tickets,
      parsed?.patchesByTicket,
      parsed?.data?.tickets,
      parsed?.data?.bilete,
   ];

   for (const source of bucketSources) {
      if (Array.isArray(source)) {
         return source
            .map((item, index) => normalizeTicketEntry(item, "", index))
            .filter(Boolean);
      }

      if (isObject(source)) {
         return Object.entries(source)
            .map(([code, ticket], index) =>
               normalizeTicketEntry(
                  {
                     ...(isObject(ticket) ? ticket : {}),
                     ticket: isObject(ticket) ? ticket?.ticket ?? code : code,
                  },
                  code,
                  index,
               ),
            )
            .filter(Boolean);
      }
   }

   const reservedKeys = new Set([
      "schemaversion",
      "type",
      "generatedat",
      "summary",
      "warnings",
      "meta",
      "export",
      "exportedat",
   ]);

   const rootTickets = Object.entries(parsed)
      .filter(([key, value]) => {
         if (reservedKeys.has(normalizeLooseKey(key))) return false;
         if (!isObject(value)) return false;
         return safeList(
            value?.questions ?? value?.intrebari ?? value?.întrebări,
         ).length;
      })
      .map(([code, ticket], index) =>
         normalizeTicketEntry(
            {
               ...ticket,
               ticket: ticket?.ticket ?? ticket?.bilet ?? code,
            },
            code,
            index,
         ),
      )
      .filter(Boolean);

   return rootTickets;
}

export function parseOfficialTicketsImport(parsed) {
   const errors = [];
   const tickets = flattenTicketEntries(parsed);

   if (!tickets.length) {
      errors.push(
         'Fișier invalid. Aștept "bilete"/"tickets" cu întrebări, sau un array de întrebări.',
      );
   }

   for (const ticket of tickets) {
      if (!ticket.questions.length) {
         errors.push(`${ticket.ticketKey}: nu are întrebări.`);
      }
   }

   const questionsTotal = tickets.reduce(
      (sum, ticket) => sum + ticket.questions.length,
      0,
   );
   const correctFallbackCount = tickets.reduce(
      (sum, ticket) =>
         sum +
         ticket.questions.filter((question) => question.correctAnswerFallbackUsed)
            .length,
      0,
   );

   return {
      tickets: tickets.filter((ticket) => ticket.questions.length),
      errors,
      summary: {
         ticketsTotal: tickets.length,
         questionsTotal,
         correctFallbackCount,
      },
   };
}

export function resolveImportTicket(importTicket, dbTickets = []) {
   const rows = safeList(dbTickets);
   if (!rows.length || !importTicket) return null;

   if (importTicket.ticketId) {
      const byId = rows.find(
         (row) => Number(row?.id) === Number(importTicket.ticketId),
      );
      if (byId) return byId;
   }

   const wantedNo = importTicket.ticketNo;
   if (wantedNo) {
      const byNo = rows.find(
         (row) => extractTicketNumber(row?.name) === wantedNo,
      );
      if (byNo) return byNo;
   }

   const wantedKey = normalizeLooseKey(importTicket.ticketKey);
   if (wantedKey && wantedKey !== "selected") {
      const byKey = rows.find((row) => {
         const nameKey = normalizeLooseKey(row?.name);
         const idKey = normalizeLooseKey(`B${row?.id}`);
         return (
            nameKey === wantedKey ||
            idKey === wantedKey ||
            nameKey.includes(wantedKey) ||
            wantedKey.includes(nameKey)
         );
      });
      if (byKey) return byKey;
   }

   if (importTicket.ticketName) {
      const wantedName = String(importTicket.ticketName).trim().toLowerCase();
      const byName = rows.find(
         (row) => String(row?.name ?? "").trim().toLowerCase() === wantedName,
      );
      if (byName) return byName;
   }

   return null;
}

export function orderQuestionsByIds(questions, questionIds = []) {
   const ids = safeList(questionIds)
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
   const byId = new Map(
      safeList(questions)
         .map((question) => {
            const id = toPositiveInt(question?.id);
            return id ? [id, question] : null;
         })
         .filter(Boolean),
   );

   const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
   const leftovers = safeList(questions).filter(
      (question) => !ids.includes(Number(question?.id)),
   );

   return [...ordered, ...leftovers];
}

export function buildImportApplicationPlan(
   importTicket,
   dbTicket,
   existingQuestions,
   questionIds = [],
) {
   const orderedExisting = orderQuestionsByIds(existingQuestions, questionIds);
   const updates = [];

   for (const importQuestion of importTicket.questions) {
      let target =
         importQuestion.questionId &&
         orderedExisting.find(
            (question) => Number(question.id) === Number(importQuestion.questionId),
         );

      if (!target) {
         target = orderedExisting[importQuestion.order - 1] || null;
      }

      if (!target?.id) {
         updates.push({
            order: importQuestion.order,
            questionId: importQuestion.questionId || null,
            status: "missing",
            error: `Nu există întrebare la poziția ${importQuestion.order}.`,
         });
         continue;
      }

      const image = firstNonEmpty(
         importQuestion.draft?.image,
         target?.image,
      );

      updates.push({
         order: importQuestion.order,
         questionId: target.id,
         status: "ready",
         payload: {
            ...importQuestion.payload,
            image,
         },
      });
   }

   return {
      ticketId: dbTicket?.id ?? null,
      ticketName: dbTicket?.name ?? importTicket.ticketKey,
      updates,
      readyCount: updates.filter((row) => row.status === "ready").length,
      missingCount: updates.filter((row) => row.status === "missing").length,
   };
}
